import React from 'react';
import { Box, Button } from '@mui/material';
import {
  PASSENGER_DEFAULT_COUNTS,
  PASSENGER_DROPDOWN_PAPER_SX,
  PASSENGER_MAX,
} from './flightForm.constants';

function travelerSelectedLabel(total) {
  const count = Number(total) || 0;
  return `${count} Traveler${count === 1 ? '' : 's'} selected`;
}

/**
 * Desktop travellers card: header, age rows with steppers, selection summary, and Done.
 */
class PassengerDropdown extends React.Component {
  emit = (counts) => {
    if (this.props.onCountsChange) this.props.onCountsChange(counts);
  };

  changeAdults = (delta) => {
    const { numAdults, numChildren, numInfants } = this.props;
    if (delta < 0 && (numAdults <= 1 || numInfants >= numAdults)) return;
    const adults = numAdults + delta;
    if (adults < 1) return;
    const infants = Math.min(numInfants, adults);
    if (adults + numChildren + infants > PASSENGER_MAX) return;
    this.emit({ adults, children: numChildren, infants });
  };

  changeChildren = (delta) => {
    if (this.props.isSpecialFare) return;
    const { numAdults, numChildren, numInfants } = this.props;
    const children = numChildren + delta;
    if (children < 0) return;
    if (numAdults + children + numInfants > PASSENGER_MAX) return;
    this.emit({ adults: numAdults, children, infants: numInfants });
  };

  changeInfants = (delta) => {
    if (this.props.isSpecialFare) return;
    const { numAdults, numChildren, numInfants } = this.props;
    const infants = numInfants + delta;
    if (infants < 0 || infants > numAdults) return;
    if (numAdults + numChildren + infants > PASSENGER_MAX) return;
    this.emit({ adults: numAdults, children: numChildren, infants });
  };

  handleReset = () => {
    this.emit({ ...PASSENGER_DEFAULT_COUNTS });
  };

  handleDone = () => {
    if (this.props.onDone) this.props.onDone();
  };

  specialFareNote() {
    if (!this.props.isSpecialFare) return null;
    const fareName = this.props.fareType === 'student' ? 'Student' : 'Senior Citizen';
    return <div className="passenger-row-note">Not available for {fareName} fare</div>;
  }

  renderStepper(label, value, onDecrease, onIncrease, decreaseDisabled, increaseDisabled) {
    return (
      <div className="passenger-stepper" role="group" aria-label={label}>
        <button
          type="button"
          className="passenger-stepper-btn passenger-stepper-btn--minus"
          aria-label={`Decrease ${label}`}
          disabled={decreaseDisabled}
          onClick={onDecrease}
        >
          −
        </button>
        <span className={`passenger-stepper-value${value === 0 ? ' passenger-stepper-value--empty' : ''}`}>
          {value}
        </span>
        <button
          type="button"
          className="passenger-stepper-btn passenger-stepper-btn--plus"
          aria-label={`Increase ${label}`}
          disabled={increaseDisabled}
          onClick={onIncrease}
        >
          +
        </button>
      </div>
    );
  }

  render() {
    const { numAdults, numChildren, numInfants, isSpecialFare } = this.props;
    const total = numAdults + numChildren + numInfants;
    const atMax = total >= PASSENGER_MAX;

    return (
      <Box
        data-passenger-dropdown
        className="passenger-dropdown"
        sx={{ ...PASSENGER_DROPDOWN_PAPER_SX, ...this.props.paperSx }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="passenger-dropdown-header">
          <div>
            <div className="passenger-dropdown-title">Travelers</div>
            <div className="passenger-dropdown-subtitle">Choose guests for your trip</div>
          </div>
          <button type="button" className="passenger-dropdown-reset" onClick={this.handleReset}>
            Reset
          </button>
        </div>

        <div className="passenger-dropdown-rows">
          <div className="passenger-row">
            <div>
              <div className="passenger-row-title">Adults</div>
              <div className="passenger-row-hint">Age 12+</div>
            </div>
            {this.renderStepper(
              'Adults',
              numAdults,
              () => this.changeAdults(-1),
              () => this.changeAdults(1),
              numAdults <= 1 || numInfants >= numAdults,
              atMax
            )}
          </div>

          <div className={`passenger-row${isSpecialFare ? ' passenger-row--locked' : ''}`}>
            <div>
              <div className="passenger-row-title">Children</div>
              <div className="passenger-row-hint">Ages 2 – 11</div>
              {this.specialFareNote()}
            </div>
            {this.renderStepper(
              'Children',
              numChildren,
              () => this.changeChildren(-1),
              () => this.changeChildren(1),
              numChildren <= 0 || isSpecialFare,
              atMax || isSpecialFare
            )}
          </div>

          <div className={`passenger-row${isSpecialFare ? ' passenger-row--locked' : ''}`}>
            <div>
              <div className="passenger-row-title">Infants</div>
              <div className="passenger-row-hint">Below 2 (on lap)</div>
              {this.specialFareNote()}
            </div>
            {this.renderStepper(
              'Infants',
              numInfants,
              () => this.changeInfants(-1),
              () => this.changeInfants(1),
              numInfants <= 0 || isSpecialFare,
              numInfants >= numAdults || atMax || isSpecialFare
            )}
          </div>
        </div>

        <div className="passenger-dropdown-summary">
          <span className="passenger-dropdown-summary-count">
            <span className="passenger-dropdown-summary-dot" aria-hidden="true" />
            {travelerSelectedLabel(total)}
          </span>
          <span className="passenger-dropdown-summary-max">Max {PASSENGER_MAX}</span>
        </div>

        <Button variant="contained" disableRipple className="passenger-done-btn" onClick={this.handleDone}>
          Done
        </Button>
      </Box>
    );
  }
}

export default PassengerDropdown;
